import {
  SecondaryButton,
  StatusMessage,
  StatusMessageDirection,
} from "@brexhq/component-library";
import styled from "@emotion/styled";
import React from "react";

import useAnalytics from "@brexhq/next/src/hooks/useAnalytics";
import {
  ButtonClickedEvent,
  ErrorComponentShown,
} from "@brexhq/next/src/telemetry/analyticsEvents";
import { logLeadFormFlowError } from "@brexhq/next/src/telemetry/logging";

const Wrapper = styled.div({
  display: "flex",
  justifyContent: "center",
});

export type DefaultErrorProps = {
  description?: string;
  direction?: StatusMessageDirection;
  errorMessage?: string;
  errorOrigin?: string;
  onClickRetry?: () => void;
  retryText?: string;
  title?: string;
  isMappedUserError?: boolean;
};

export const DefaultError: React.FC<DefaultErrorProps> = ({
  description,
  direction = StatusMessageDirection.VERTICAL,
  errorMessage,
  errorOrigin,
  onClickRetry,
  retryText = "Try again",
  title,
  isMappedUserError = false,
}) => {
  const { trackEvent } = useAnalytics();

  const metadata = React.useMemo(
    () => ({
      description,
      errorMessage,
      errorOrigin,
      title,
      isMappedUserError,
    }),
    [description, errorMessage, errorOrigin, title, isMappedUserError],
  );

  React.useEffect(() => {
    // try to derive a useful error message from passed props
    const errorMsg =
      errorMessage || description || title || "DefaultError shown";

    trackEvent(new ErrorComponentShown(400, errorMsg, "Error Page", metadata));
    logLeadFormFlowError(new Error(errorMsg));
  }, [
    trackEvent,
    metadata,
    errorMessage,
    description,
    title,
    isMappedUserError,
  ]);

  const handleClickRetry = React.useCallback(() => {
    trackEvent(
      new ButtonClickedEvent("retry", "DefaultError", "error", "retry"),
    );
    onClickRetry?.();
  }, [onClickRetry, trackEvent]);

  const actions = onClickRetry
    ? [
        {
          id: "retry",
          action: (
            <SecondaryButton onClick={handleClickRetry}>
              {retryText}
            </SecondaryButton>
          ),
        },
      ]
    : [];

  return (
    <Wrapper data-testid={"default-error"}>
      <StatusMessage
        direction={direction}
        graphic={
          "https://brand.brex.com/transform/bb640634-d6e4-4850-bc8d-b07d66316f32/Back-end-Crash_00000"
        }
        title={title || "Something went wrong"}
        description={description || "Please try again or contact support."}
        actions={actions}
      />
    </Wrapper>
  );
};
